import React, { Suspense, useEffect } from 'react';
import { logUserEvent } from '@analytics/amplifyLogging';
import OktaWrapper from './OktaWrapper';
import { Provider } from 'react-redux';
import { persistor, store } from '@store';
import { useNavigate } from 'react-router-dom';
import { PersistGate } from 'redux-persist/integration/react';
import { Security } from '@okta/okta-react';
import { auth } from './config';
import { toRelativeUrl } from '@okta/okta-auth-js';
import { QueryClientProvider } from '@tanstack/react-query';
import { Client } from 'fmc-digital-common';
import { generateTheme, SnackbarWithActionBase } from '@fmc-digital/ui';
import { SnackbarLoaderBase } from '@components_v2/UI/atoms/SnackBarLoader/index';
import { i18n } from '@fmc-digital/servicing';
import { I18nextProvider } from 'react-i18next';
import { ThemeProvider as MuiThemeProvider, useMediaQuery, useTheme } from '@mui/material';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { default as ACPErrorBoundary } from '@containers/ErrorBoundary';
import ErrorIcon from '@mui/icons-material/Error';
import { MaterialDesignContent, SnackbarProvider, useSnackbar } from 'notistack';
import styled from '@mui/system/styled';
import CloseIcon from '@mui/icons-material/Close';

const StyledMaterialDesignContent = styled(MaterialDesignContent)(() => ({
    '&.notistack-MuiContent-success': {
        backgroundColor: '#008735'
    },
    '&.notistack-MuiContent-error': {
        backgroundColor: '#D73F3D'
    }
}));

const CloseSnackIcon = ({ snackbarKey }: any) => {
    const { closeSnackbar } = useSnackbar();
    return (
        <CloseIcon
            onClick={() => closeSnackbar(snackbarKey)}
            style={{ color: '#fff', position: 'absolute', top: '0.5rem', right: '0.5rem', fontSize: '1.25rem' }}
        />
    );
};

const SnackBarProviderWrapper = ({ children }: any) => {
    const theme = useTheme();
    const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
    return (
        <SnackbarProvider
            Components={{
                success: StyledMaterialDesignContent,
                error: StyledMaterialDesignContent,
                SnackbarWithAction: SnackbarWithActionBase,
                SnackbarLoader: SnackbarLoaderBase
            }}
            classes={{ containerRoot: isTablet ? 'snackbar-medium-container' : '' }}
            iconVariant={{
                error: (
                    <ErrorIcon
                        sx={{
                            width: '1.375rem',
                            height: '1.375rem',
                            marginRight: '0.50rem'
                        }}
                    />
                )
            }}
            maxSnack={5}
            action={snackbarKey => <CloseSnackIcon snackbarKey={snackbarKey} />}
            preventDuplicate
        >
            {children}
        </SnackbarProvider>
    );
};
Client.setDefaultOptions({
    queries: {
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
        cacheTime: 300000,
        staleTime: 0,
        retryDelay: 1000,
        retry: 1,
        networkMode: 'always'
    }
});

['authToken', 'amplify', 'isNtbRequired', 'selfServUrl', 'xAPIKey'].forEach(value => {
    Client.setQueryDefaults([`${value}`], { cacheTime: 30 * 60 * 1000, staleTime: Infinity });
});

const App: React.FC = () => {
    const oktaAuth = auth.getOkta();
    const navigate = useNavigate();
    const restoreOriginalUri = (_oktaAuth: any, originalUri: string) => {
        let uri = '/';
        console.log('restoreOriginalUri , ', { originalUri }, window.location.origin);
        if (originalUri) {
            // strip the lead '/#' from the uri
            uri = originalUri.startsWith('/#') ? originalUri.slice(2) : originalUri;
        }
        navigate(toRelativeUrl(uri, window.location.origin), { replace: true });
    };

    useEffect(() => {
        logUserEvent({
            type: 'ACP Application started',
            description: 'Initializing Auth customer portal.',
            action: 'InitializingACP'
        });
    }, []);

    return (
        <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
            <Provider store={store}>
                <PersistGate persistor={persistor}>
                    <QueryClientProvider client={Client}>
                        <MuiThemeProvider theme={generateTheme()}>
                            <I18nextProvider i18n={i18n}>
                                <ACPErrorBoundary>
                                    <SnackBarProviderWrapper>
                                        <OktaWrapper />
                                    </SnackBarProviderWrapper>
                                </ACPErrorBoundary>
                            </I18nextProvider>
                        </MuiThemeProvider>
                        <ReactQueryDevtools />
                    </QueryClientProvider>
                </PersistGate>
            </Provider>
        </Security>
    );
};
export default App;
